import type AHDate from "_shared/dates/date";
import type Guid from "_shared/guids/guid";
import type ReadReceipt from "messaging/read-receipts/read-receipt";

/** What the unread rule reads off a message or a comment. Both `Message` and `Comment` have it. */
export type UnreadCandidate = {
  readonly senderId: Guid | null;
  readonly isDeleted: boolean;
  readonly published?: AHDate;
  readonly retracted?: AHDate;
  readonly readReceipts: readonly ReadReceipt[];
};

/**
 * Whether `readerId` has still to read the message. This is the one unread rule (#5828). The server
 * holds the same rule in `MessageQueryExtensions.UnreadBy`, and every unread count it sends is a count
 * of it, so a tab's count and the rows drawn as unread agree.
 *
 * - A system notice, which has no sender, is unread until the reader marks it read.
 * - The reader's own message is never unread.
 * - A message its audience cannot see is never unread: a draft, a message its author took back, and a
 *   deleted message. There is nothing left to read.
 */
export function isUnreadBy(entry: UnreadCandidate, readerId: Guid | undefined): boolean {
  if (!readerId) return false;
  if (entry.senderId?.isEqualTo(readerId)) return false;
  if (entry.isDeleted || !entry.published || entry.retracted) return false;
  return !entry.readReceipts.some((receipt) => receipt.userId.isEqualTo(readerId));
}
